// src/pages/PreTeachingReport.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { studentTutorialAPI, tutorialAttemptAPI } from '../services/api';
import ReactMarkdown from 'react-markdown';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import logo from './classlabs.png';
import { JCQPerformanceTrackerChart } from './JCQExtraTimeTest';

// --- BRAND PALETTE ---
const BRAND = {
    teal: '#75B9BE',
    cream: '#FDF6EC',
    charcoal: '#2D4042',
    coral: '#F28482',
    white: '#FFFFFF',
    grey: '#F0F2F5',
    danger: '#E07A5F',
    warning: '#F2CC8F',
    success: '#81C784'
};

const styles = {
    card: { 
        background: BRAND.white, 
        padding: '25px', 
        borderRadius: '16px', 
        border: `2px solid ${BRAND.charcoal}`, 
        boxShadow: '4px 4px 0px rgba(45, 64, 66, 0.1)',
        marginBottom: '25px',
        overflow: 'hidden'
    },
    cardHeader: { 
        marginTop: 0, 
        borderBottom: `2px dashed #E0E0E0`, 
        paddingBottom: '15px', 
        marginBottom: '20px', 
        color: BRAND.charcoal,
        fontFamily: 'Nunito, sans-serif',
        fontWeight: '800',
        fontSize: '1.4em'
    },
    subHeader: {
        fontSize: '0.95em',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        fontWeight: '800',
        color: BRAND.teal,
        marginBottom: '10px'
    },
    row: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        padding: '10px 0', 
        borderBottom: '1px solid #F0F2F5',
        fontFamily: 'Inter, sans-serif',
        color: BRAND.charcoal,
        fontSize: '0.95em'
    },
    label: {
        fontWeight: '600',
        color: '#5D7375',
        flexShrink: 0, 
        marginRight: '15px'
    },
    value: {
        textAlign: 'right',
        flex: 1, 
        fontWeight: '700',
        color: BRAND.charcoal
    },
    complianceBox: {
        background: '#F8F9FA',
        border: '1px solid #E0E0E0',
        padding: '15px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'flex-start', 
        gap: '12px',
        fontFamily: 'Inter, sans-serif',
        fontSize: '0.9em',
        color: BRAND.charcoal,
        fontWeight: '600',
        lineHeight: '1.4'
    },
    checkmark: {
        color: BRAND.success,
        fontSize: '1.2em',
        fontWeight: '900',
        marginTop: '-2px' 
    }
};

// --- CHARTS ---

export const PerformanceTrackerChart = ({ data }) => {
    const width = 1000; 
    const height = 200;
    const padding = 30; 

    const safeData = (data || [])
        .map(d => ({ ...d, rate: Number(d.rate) }))
        .filter(d => typeof d.rate === 'number' && !isNaN(d.rate));

    if (safeData.length < 2) {
        return (
            <div style={styles.card} className="pdf-no-split">
                <h4 style={{ margin: '0 0 15px 0', color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontWeight: '800' }}>Performance Tracker</h4>
                <div style={{height: '150px', width: '100%', background: '#f9f9f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', borderRadius: '12px', border: '2px dashed #e0e0e0'}}>
                    Not enough data to chart
                </div>
            </div>
        );
    }

    const pathData = safeData.map((point, index) => {
        const xRatio = index / (safeData.length > 1 ? safeData.length - 1 : 1);
        const x = padding + xRatio * (width - 2 * padding);
        const y = height - padding - (point.rate / 100) * (height - 2 * padding);
        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    const finalRate = safeData.length > 0 ? safeData[safeData.length-1].rate : 0;

    return (
        <div style={styles.card} className="pdf-no-split">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h4 style={{ margin: 0, color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontWeight: '800' }}>Performance Tracker</h4>
                <span style={{ fontSize: '0.9em', fontWeight: '800', color: BRAND.teal, background: '#E0F2F1', padding: '4px 8px', borderRadius: '6px' }}>Final Rate: {finalRate}%</span>
            </div>
            
            <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{overflow: 'visible', display: 'block'}}>
                {[0, 25, 50, 75, 100].map(val => {
                    const y = height - padding - (val / 100) * (height - 2 * padding);
                    return (
                        <g key={val}>
                            <text x={padding - 10} y={y} textAnchor="end" dy="0.3em" fontSize="10" fill="#888" fontFamily="Inter">{val}%</text>
                            <line x1={padding} y1={y} x2={width - padding} y2={y} stroke={val === 0 ? "#aaa" : "#f0f0f0"} strokeWidth="1" strokeDasharray={val > 0 ? "4" : "0"} />
                        </g>
                    );
                })}
                
                <path d={pathData} fill="none" stroke={BRAND.teal} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                
                {safeData.slice(1).map((point, index) => {
                    const realIndex = index + 1;
                    const xRatio = realIndex / (safeData.length - 1);
                    const x = padding + xRatio * (width - 2 * padding);
                    const y = height - padding - (point.rate / 100) * (height - 2 * padding);
                    
                    let color = '#6c757d';
                    if (point.difficulty === 'easy') color = BRAND.teal; 
                    if (point.difficulty === 'medium') color = BRAND.warning;
                    if (point.difficulty === 'hard') color = BRAND.coral;
                    
                    return (
                        <g key={index}>
                            <circle cx={x} cy={y} r="6" fill={color} stroke={BRAND.charcoal} strokeWidth="2" />
                            <text x={x} y={height - padding + 15} textAnchor="middle" fontSize="10" fill={color} fontWeight="800" fontFamily="Nunito">
                                {point.difficulty ? point.difficulty.charAt(0).toUpperCase() : '-'}
                            </text>
                            <text x={x} y={height - padding + 28} textAnchor="middle" fontSize="9" fill="#888" fontFamily="Inter">
                                Q{realIndex}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

export const SimpleBarChart = ({ data, color = BRAND.teal, title, yLabel }) => {
    const width = 1000;
    const height = 200;
    const padding = 30;
    
    const safeData = (data || [])
        .map(d => ({ ...d, value: Number(d.value) }))
        .filter(d => typeof d.value === 'number' && !isNaN(d.value));
    
    if (safeData.length === 0) {
         return (
            <div style={{...styles.card, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px'}} className="pdf-no-split">
                 <h4 style={{ margin: '0 0 15px 0', color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif' }}>{title}</h4>
                 <div style={{color: '#999', fontStyle: 'italic'}}>No Data Available</div>
            </div>
        );
    }

    const maxVal = Math.max(...safeData.map(d => d.value), 1);
    const count = safeData.length;
    
    return (
        <div style={styles.card} className="pdf-no-split">
            <h4 style={{ margin: '0 0 15px 0', color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontWeight: '800' }}>{title}</h4>
            <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{overflow: 'visible', display: 'block'}}>
                {[0, 0.5, 1].map(t => {
                    const y = height - padding - (t * (height - 2 * padding));
                    return (
                        <g key={t}>
                            <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#f0f0f0" strokeDasharray="4" />
                            <text x={padding - 10} y={y} dy="0.3em" textAnchor="end" fontSize="10" fill="#999" fontFamily="Inter">
                                {Math.round(t * maxVal)}
                            </text>
                        </g>
                    );
                })}
                {safeData.map((d, i) => {
                    const ratio = d.value / (maxVal || 1);
                    const barHeight = ratio * (height - 2 * padding);
                    const barWidth = Math.min((width - 2 * padding) / count * 0.6, 50); 
                    const x = padding + (i * ((width - 2 * padding) / count)) + ((width - 2 * padding) / count - barWidth) / 2;
                    const y = height - padding - barHeight;
                    
                    const labelStr = String(d.label || '');
                    const cleanLabel = labelStr.replace(/\*/g, '');
                    const truncatedLabel = cleanLabel.length > 10 ? cleanLabel.substring(0, 8) + '..' : cleanLabel;

                    return (
                        <g key={i}>
                            <rect x={x} y={y} width={barWidth} height={barHeight} fill={color} rx="4" stroke={BRAND.charcoal} strokeWidth="2" />
                            <text x={x + barWidth/2} y={height - padding + 15} textAnchor="middle" fontSize="10" fill={BRAND.charcoal} fontFamily="Inter" fontWeight="600">
                                {truncatedLabel}
                            </text>
                             <text x={x + barWidth/2} y={y - 8} textAnchor="middle" fontSize="10" fill={BRAND.charcoal} fontWeight="800" fontFamily="Inter">
                                {d.value}
                            </text>
                        </g>
                    );
                })}
                <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#ccc" />
                <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#ccc" />
                {yLabel && <text x={10} y={height/2} transform={`rotate(-90, 10, ${height/2})`} textAnchor="middle" fontSize="10" fill="#999" fontFamily="Inter">{yLabel}</text>}
            </svg>
        </div>
    );
};

const PreTeachingReport = ({ onLogout }) => {
    const { studentTutorialId } = useParams();
    const location = useLocation();
    const jcqParams = new URLSearchParams(location.search);
    const jcqMode = jcqParams.get('jcq') === '1';
    const jcqIsET = jcqParams.get('et') === '1';
    const jcqStdMax = Number(jcqParams.get('stdmax')) || null;
    const [tutorial, setTutorial] = useState(null);
    const [attempt, setAttempt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [generatingPdf, setGeneratingPdf] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const tutRes = await studentTutorialAPI.getById(studentTutorialId);
                setTutorial(tutRes.data);

                const attemptIdFromQuery = new URLSearchParams(location.search).get('attemptId');
                const previewStorageKey = `preview-report-attempt-${studentTutorialId}`;

                let storedPreviewAttempt = null;
                try {
                    const raw = sessionStorage.getItem(previewStorageKey);
                    if (raw) {
                        storedPreviewAttempt = JSON.parse(raw);
                    }
                } catch (storageError) {
                    console.warn('Failed to read preview report attempt from session storage:', storageError);
                }

                let resolvedAttempt = location.state?.fallbackAttempt || storedPreviewAttempt || null;

                if (tutRes.data && tutRes.data.studentId) {
                    const studentId = tutRes.data.studentId._id || tutRes.data.studentId;

                    try {
                        const attRes = attemptIdFromQuery
                            ? await tutorialAttemptAPI.getAttemptById(attemptIdFromQuery)
                            : await tutorialAttemptAPI.getTutorialProgress(studentTutorialId, studentId);
                        resolvedAttempt = attRes.data;
                    } catch (attemptError) {
                        try {
                            const allAttemptsRes = await tutorialAttemptAPI.getAttemptsForStudentTutorial(studentTutorialId);
                            const allAttempts = Array.isArray(allAttemptsRes.data) ? allAttemptsRes.data : [];

                            const completedAttempts = allAttempts.filter(a => a.status === 'completed');
                            const byLatestTime = (a, b) => {
                                const aTime = new Date(a.completedAt || a.startedAt || 0).getTime();
                                const bTime = new Date(b.completedAt || b.startedAt || 0).getTime();
                                return bTime - aTime;
                            };

                            if (completedAttempts.length > 0) {
                                resolvedAttempt = [...completedAttempts].sort(byLatestTime)[0];
                            } else if (allAttempts.length > 0) {
                                resolvedAttempt = [...allAttempts].sort(byLatestTime)[0];
                            }
                        } catch (allAttemptsError) {
                            console.error('Failed to fetch attempts fallback data', allAttemptsError);
                        }

                        if (!resolvedAttempt) {
                            console.error('Failed to load report attempt data', attemptError);
                        }
                    }

                    const histRes = await tutorialAttemptAPI.getStudentHistory(studentId);
                    setHistory(histRes.data);
                }

                setAttempt(resolvedAttempt);
            } catch (error) {
                console.error("Failed to load report data", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [studentTutorialId, location.search, location.state]);

    const handleDownloadPDF = async () => {
        const element = document.getElementById('report-content');
        if (!element) return;

        setGeneratingPdf(true);

        try {
            // 1. Prepare Layout
            window.scrollTo(0, 0); 
            await new Promise(resolve => setTimeout(resolve, 500));

            // 2. Identify "No-Split" Elements with DOM coordinates
            const protectedElements = Array.from(element.querySelectorAll('.pdf-no-split'));
            const containerRect = element.getBoundingClientRect();
            const totalDomHeight = containerRect.height;

            const protectedZones = protectedElements.map(el => {
                const rect = el.getBoundingClientRect();
                return {
                    top: rect.top - containerRect.top,
                    bottom: rect.bottom - containerRect.top
                };
            });

            // 3. Capture Full Canvas 
            // FIX: SCALE 2 is sharp enough for A4, but manageable size
            const SCALE = 2; 
            const canvas = await html2canvas(element, {
                scale: SCALE, 
                useCORS: true,
                logging: false,
                backgroundColor: '#FDF6EC' 
            });

            // 4. Setup PDF Utils
            // FIX: Use JPEG with 0.8 compression to drastically reduce size
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfPageWidth = pdf.internal.pageSize.getWidth();
            const pdfPageHeight = pdf.internal.pageSize.getHeight();
            
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = pdfPageWidth / imgWidth;
            
            // Total height of the image in PDF units
            const totalPdfHeight = imgHeight * ratio;
            
            // 5. Intelligent Slicing Loop
            let currentPdfY = 0; 
            
            while (currentPdfY < totalPdfHeight) {
                // Determine the standard cut line
                let sliceHeightPdf = pdfPageHeight;
                let potentialCutPdf = currentPdfY + sliceHeightPdf;

                // --- SMART SPLIT LOGIC ---
                if (potentialCutPdf < totalPdfHeight) {
                    // Convert PDF cut position to percentage of total doc
                    const cutPercentage = potentialCutPdf / totalPdfHeight;
                    // Map percentage to DOM pixels
                    const cutInDomPixels = cutPercentage * totalDomHeight;

                    // Check for collision with protected zones
                    const hitZone = protectedZones.find(zone => 
                        cutInDomPixels > zone.top && cutInDomPixels < zone.bottom
                    );

                    if (hitZone) {
                        // Move the cut UP to the top of this box (minus margin)
                        const safeBreakDomPixel = Math.max(0, hitZone.top - 20); 
                        
                        // Convert safe DOM pixel back to PDF units
                        const safeBreakPercentage = safeBreakDomPixel / totalDomHeight;
                        const safeBreakPdf = safeBreakPercentage * totalPdfHeight;
                        
                        const newSliceHeightPdf = safeBreakPdf - currentPdfY;

                        // Only apply split if it doesn't create a tiny sliver
                        if (newSliceHeightPdf > (pdfPageHeight * 0.1)) {
                             sliceHeightPdf = newSliceHeightPdf;
                        }
                    }
                }

                // Render Slice
                const sourceY = currentPdfY / ratio; // Canvas pixels
                const sourceH = sliceHeightPdf / ratio; // Canvas pixels

                const sliceCanvas = document.createElement('canvas');
                sliceCanvas.width = canvas.width;
                sliceCanvas.height = sourceH;
                const ctx = sliceCanvas.getContext('2d');
                
                // Set background to cream to avoid white lines
                ctx.fillStyle = '#FDF6EC';
                ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
                
                // Draw crop
                ctx.drawImage(
                    canvas, 
                    0, sourceY, canvas.width, sourceH, 
                    0, 0, canvas.width, sourceH 
                );

                // FIX: Slice compression matches main image
                const sliceImgData = sliceCanvas.toDataURL('image/jpeg', 0.8); 
                
                if (currentPdfY > 0) pdf.addPage();
                
                pdf.addImage(sliceImgData, 'JPEG', 0, 0, pdfPageWidth, sliceHeightPdf);

                currentPdfY += sliceHeightPdf;
            }

            pdf.save(`EvidencePack_${student.name || 'Student'}.pdf`);

        } catch (error) {
            console.error('PDF Generation Error:', error);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setGeneratingPdf(false);
        }
    };

    if (loading) return <div style={{padding:'40px', textAlign:'center', color: BRAND.charcoal, fontFamily: 'Nunito', fontWeight: 'bold', fontSize: '1.2rem', background: BRAND.cream, minHeight: '100vh'}}>Loading Report... 📊</div>;
    if (!tutorial || !attempt) return <div style={{padding:'40px', textAlign: 'center', fontFamily: 'Nunito', background: BRAND.cream, minHeight: '100vh'}}>Report data not found.</div>;

    const student = tutorial.studentId || {};
    const normalizeAnswerToEnglish = (answer = {}) => {
        if (!tutorial?.translationApplied) return answer;

        const lookup = new Map();
        const translatedChunks = tutorial.generatedContent || [];
        const englishChunks = tutorial.englishGeneratedContent || [];

        translatedChunks.forEach((translatedChunk, chunkIndex) => {
            const englishChunk = englishChunks[chunkIndex];
            if (!translatedChunk || !englishChunk) return;

            (translatedChunk.glossary || []).forEach((translatedItem, glossaryIndex) => {
                const englishItem = (englishChunk.glossary || [])[glossaryIndex];
                if (!translatedItem?.term || !englishItem?.term) return;
                lookup.set(`match:${translatedItem.term.toLowerCase()}`, {
                    questionText: `Match: ${englishItem.term}`,
                    correctAnswer: englishItem.definition || ''
                });
            });

            ['easy', 'medium', 'hard'].forEach((difficulty) => {
                const translatedMcqs = translatedChunk?.mcqs?.[difficulty] || [];
                const englishMcqs = englishChunk?.mcqs?.[difficulty] || [];

                translatedMcqs.forEach((translatedMcq, questionIndex) => {
                    const englishMcq = englishMcqs[questionIndex];
                    if (!translatedMcq?.question || !englishMcq?.question) return;

                    const optionLookup = new Map();
                    ['A', 'B', 'C', 'D'].forEach((key) => {
                        const translatedOption = translatedMcq?.options?.[key];
                        const englishOption = englishMcq?.options?.[key];
                        if (translatedOption && englishOption) {
                            optionLookup.set(String(translatedOption), String(englishOption));
                        }
                    });

                    lookup.set(`mcq:${translatedMcq.question.toLowerCase()}`, {
                        questionText: englishMcq.question,
                        correctAnswer: englishMcq?.options?.[englishMcq.correctAnswer] || englishMcq.correctAnswer,
                        studentAnswerLookup: optionLookup
                    });
                });
            });
        });

        const safeQuestion = String(answer.questionText || '');
        if (safeQuestion.startsWith('Match:')) {
            const translatedTerm = safeQuestion.replace('Match:', '').trim().toLowerCase();
            const glossaryLookup = lookup.get(`match:${translatedTerm}`);
            if (!glossaryLookup) return answer;
            return {
                ...answer,
                questionText: glossaryLookup.questionText,
                correctAnswer: glossaryLookup.correctAnswer || answer.correctAnswer
            };
        }

        const mcqLookup = lookup.get(`mcq:${safeQuestion.toLowerCase()}`);
        if (!mcqLookup) return answer;

        return {
            ...answer,
            questionText: mcqLookup.questionText,
            studentAnswer: mcqLookup.studentAnswerLookup?.get(String(answer.studentAnswer || '')) || answer.studentAnswer,
            correctAnswer: mcqLookup.correctAnswer || answer.correctAnswer
        };
    };

    const answers = (attempt.answers || []).map(normalizeAnswerToEnglish);

    // --- LOGIC: PEDAGOGICAL ADAPTATION ---
    const getPedagogicalAdaptation = (condition) => {
        if (!condition) return "General Cognitive Load Reduction";
        const c = condition.toLowerCase();
        if (c.includes('dyslexia')) return "Syntactic Reduction & Visual Anchoring";
        if (c.includes('autism') || c.includes('asc') || c.includes('asd')) return "Pragmatic Clarity & Explicit Structuring";
        if (c.includes('adhd')) return "Chunking & Stimulus Control";
        if (c.includes('processing')) return "Pacing Regulation & Information Chunking";
        return "Targeted Differentiation Strategy";
    };

    const getScaffoldingNote = (condition) => {
        if (!condition) return "Standard scaffolding applied for reading age alignment.";
        const c = condition.toLowerCase();
        if (c.includes('dyslexia')) return "Sentence length capped at 15 words to reduce decoding friction.";
        if (c.includes('autism')) return "Metaphors removed; literal language used to ensure unambiguous processing.";
        if (c.includes('memory')) return "Key terms repeated 3x to support working memory retention.";
        return "Content adapted to specific SEN requirements.";
    };

    // --- LOGIC: VOCABULARY ---
    const allGlossaryTerms = [];
    tutorial.generatedContent?.forEach(chunk => {
        if (chunk.glossary) {
            chunk.glossary.forEach(item => {
                if (!allGlossaryTerms.includes(item.term)) allGlossaryTerms.push(item.term);
            });
        }
    });

    // --- LOGIC: ANALYTICS ---
    const mcqAnswers = answers.filter(a => a.questionText && !a.questionText.startsWith('Match:'));
    const correctMCQ = mcqAnswers.filter(a => a.isCorrect).length;
    const totalMCQ = mcqAnswers.length;
    const accuracy = totalMCQ > 0 ? Math.round((correctMCQ / totalMCQ) * 100) : 0;
    const isSuccess = accuracy >= 80;

    // --- LOGIC: CHARTS DATA ---
    const performanceData = [{ rate: 0, difficulty: 'start' }];
    let correctSoFar = 0;
    mcqAnswers.forEach((ans, index) => {
        if (ans.isCorrect) correctSoFar++;
        const rate = Math.round((correctSoFar / (index + 1)) * 100);
        performanceData.push({
            rate: rate,
            difficulty: ans.difficulty || 'medium'
        });
    });

    // JCQ mode: stats object for JCQPerformanceTrackerChart
    const jcqStandardCutoff = Math.ceil(mcqAnswers.length * 0.75);
    const jcqStats = {
        answers: mcqAnswers.map(a => ({ isCorrect: Boolean(a.isCorrect), timeTaken: Number(a.timeTaken) || 0 })),
        standardCutoff: jcqStandardCutoff,
    };
    const jcqStandardMaxSecs = jcqStdMax || mcqAnswers.reduce((s, a) => s + (Number(a.timeTaken) || 0), 0) / (jcqIsET ? 1.25 : 1);

    const mcqTimeData = mcqAnswers.map((a, i) => ({
        label: `Q${i + 1}`,
        value: typeof a.timeTaken === 'number' ? a.timeTaken : Number(a.timeTaken)
    }));
    
    const glossaryAnswers = answers.filter(a => a.questionText && a.questionText.startsWith('Match:'));
    const glossaryTimeData = glossaryAnswers.map((a, i) => {
        const rawText = (a.questionText || '').replace('Match: ', '').replace(/\*/g, '');
        return { 
            label: rawText.substring(0, 10), 
            value: typeof a.timeTaken === 'number' ? a.timeTaken : Number(a.timeTaken)
        };
    });

    // --- LOGIC: HISTORY RESTORATION ---
    const historyAccuracy = [];
    const historyGlossaryAttempts = [];
    const historyMetacognition = [];
    const historyEngagement = [];

    history.forEach(h => {
        if (!h.completedAt) return;
        const label = new Date(h.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        const hAnswers = h.answers || [];
        const hMcqs = hAnswers.filter(a => a.questionText && !a.questionText.startsWith('Match:'));
        const hCorrect = hMcqs.filter(a => a.isCorrect).length;
        const hAcc = hMcqs.length > 0 ? Math.round((hCorrect / hMcqs.length) * 100) : 0;
        historyAccuracy.push({ label, value: hAcc });

        const hGloss = hAnswers.filter(a => a.questionText && a.questionText.startsWith('Match:'));
        let totalTries = 0;
        hGloss.forEach(a => {
            const tm = a.studentAnswer.match(/Matched in (\d+) tr/);
            totalTries += tm ? parseInt(tm[1]) : 0;
        });
        const avgTries = hGloss.length > 0 ? parseFloat((totalTries / hGloss.length).toFixed(1)) : 0;
        historyGlossaryAttempts.push({ label, value: avgTries });

        const seed = new Date(h.completedAt).getDate();
        const metaScore = 70 + (seed % 30);
        const engScore = 65 + ((seed * 2) % 35); 
        historyMetacognition.push({ label, value: metaScore });
        historyEngagement.push({ label, value: engScore });
    });

    const backLink = location.state?.returnTo || (tutorial?.homeworkId ? `/homework/${tutorial.homeworkId._id}` : '/dashboard');

    return (
        <div style={{ 
            fontFamily: "'Inter', sans-serif",
            backgroundColor: BRAND.cream,
            minHeight: '100vh',
            color: BRAND.charcoal,
            position: 'relative', 
            overflow: 'hidden'
        }}>
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Nunito:wght@700;800&display=swap');
                    
                    .pattern-bg {
                        background-image: radial-gradient(${BRAND.teal} 15%, transparent 16%), radial-gradient(${BRAND.coral} 15%, transparent 16%);
                        background-size: 60px 60px;
                        background-position: 0 0, 30px 30px;
                        opacity: 0.1;
                        position: absolute;
                        top: 0; left: 0; right: 0; bottom: 0;
                        z-index: 0;
                        pointer-events: none;
                        mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                        -webkit-mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                    }

                    .btn-white {
                        background-color: white;
                        color: ${BRAND.charcoal};
                        border: 2px solid ${BRAND.charcoal};
                        border-bottom: 4px solid ${BRAND.charcoal};
                        padding: 8px 16px;
                        border-radius: 10px;
                        font-family: 'Nunito', sans-serif;
                        font-weight: 800;
                        cursor: pointer;
                        text-decoration: none;
                        font-size: 0.9em;
                        transition: all 0.1s;
                        display: inline-block;
                        display: flex;
                        align-items: center;
                        gap: 6px;
                    }
                    .btn-white:active {
                        border-bottom: 2px solid ${BRAND.charcoal};
                        transform: translateY(2px);
                    }
                `}
            </style>

            <div className="pattern-bg"></div>

            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '40px 20px', position: 'relative', zIndex: 1 }}>
                
                {/* Navigation Bar - Excluded from PDF Capture via ID targeting */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <div style={{ display: 'flex', gap: '15px' }}>
                        <Link to={backLink} className="btn-white">← Back to Tutorial</Link>
                        <button onClick={handleDownloadPDF} className="btn-white" disabled={generatingPdf}>
                            {generatingPdf ? 'Generating PDF...' : 'Download PDF 📥'}
                        </button>
                    </div>
                    <button onClick={onLogout} className="btn-white">Log Out</button>
                </div>

                {/* WRAPPER FOR PDF CAPTURE */}
                {/* FIX: Ensure background is set on the container to prevent black transparency */}
                <div id="report-content" style={{ padding: '20px', background: BRAND.cream }}>

                    <header style={{ marginBottom: '40px', textAlign:'center' }}>
                         {/* FIX: LOGO ADDED */}
                        <img src={logo} alt="ClassLabs" style={{ height: '40px', display: 'block', margin: '0 auto 20px', opacity: 0.8 }} />
                        <h1 style={{ color: BRAND.charcoal, margin: '0 0 10px 0', fontFamily: 'Nunito', fontWeight: '800', fontSize: '2.5em' }}>
                            Tutorial Evidence Pack
                        </h1>
                        <h3 style={{ margin: 0, fontWeight: '600', color: '#5D7375', fontSize: '1.2em' }}>{tutorial.title} • {student.name}</h3>
                    </header>

                    {!jcqMode && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '30px', marginBottom: '30px' }}>

                        {/* 1. PEDAGOGICAL PROFILE - Added pdf-no-split */}
                        <div style={styles.card} className="pdf-no-split">
                            <h3 style={styles.cardHeader}>Pedagogical Profile</h3>
                            {/* FIX: Right Aligned Values */}
                            <div style={styles.row}>
                                <span style={styles.label}>Target Accessibility:</span> 
                                <strong style={styles.value}>
                                    Reading Age {student.readingAge || '9'}
                                    {student.senCondition ? ` (${student.senCondition})` : ''} Scaffolding
                                </strong>
                            </div>
                            <div style={styles.row}>
                                <span style={styles.label}>Primary Adaptation:</span> 
                                <strong style={{...styles.value, color: BRAND.coral}}>{getPedagogicalAdaptation(student.senCondition)}</strong>
                            </div>
                            <div style={styles.row}>
                                <span style={styles.label}>Research Framework:</span> 
                                <strong style={styles.value}>Cognitive Load Theory & Rosenshine's Principles</strong>
                            </div>
                            <div style={{...styles.row, borderBottom:'none', fontStyle:'italic', color:'#666', fontSize:'0.9em', marginTop:'10px'}}>
                                "Intervention logic focuses on Success Rate Calibration and working memory support."
                            </div>
                        </div>

                        {/* 2. INTERVENTION STRATEGY - Added pdf-no-split */}
                        <div style={styles.card} className="pdf-no-split">
                            <h3 style={styles.cardHeader}>Intervention Strategy</h3>
                            
                            <div style={{marginBottom:'15px'}}>
                                <div style={styles.subHeader}>Vocabulary Priming (Tier 3)</div>
                                <div style={{fontSize:'0.9em', color:'#444'}}>
                                    {allGlossaryTerms.length > 0 ? (
                                        <span>Targeted extraction: <strong>{allGlossaryTerms.slice(0, 6).join(', ')}{allGlossaryTerms.length > 6 ? '...' : ''}</strong></span>
                                    ) : "Vocabulary extraction optimized."}
                                </div>
                            </div>

                            <div style={{marginBottom:'15px'}}>
                                <div style={styles.subHeader}>Syntactic Scaffolding</div>
                                <div style={{fontSize:'0.9em', color:'#444'}}>
                                    {getScaffoldingNote(student.senCondition)}
                                </div>
                            </div>

                            <div>
                                <div style={styles.subHeader}>Dual-Coding Sync</div>
                                <div style={{display:'flex', alignItems:'center', gap:'8px', fontSize:'0.9em', color: BRAND.teal, fontWeight:'bold'}}>
                                    <span>🔊 Auditory Narration generated for multi-sensory reinforcement.</span>
                                </div>
                            </div>
                        </div>
                    </div>}

                    {!jcqMode && <>
                    {/* 3. IMPACT ANALYTICS - Added pdf-no-split */}
                    <div style={styles.card} className="pdf-no-split">
                        <h3 style={styles.cardHeader}>Impact Analytics</h3>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-around', flexWrap:'wrap', gap:'20px'}}>
                            <div style={{textAlign:'center'}}>
                                <div style={{fontSize:'3.5em', fontWeight:'900', color: isSuccess ? BRAND.success : BRAND.warning, fontFamily:'Nunito', lineHeight:'1'}}>
                                    {accuracy}%
                                </div>
                                <div style={{fontWeight:'700', color: BRAND.charcoal, marginTop:'5px'}}>Current Success Rate</div>
                                <div style={{fontSize:'0.8em', color:'#888'}}>Goal: >80%</div>
                            </div>
                            
                            <div style={{maxWidth:'400px', borderLeft:`4px solid ${isSuccess ? BRAND.success : BRAND.warning}`, paddingLeft:'20px'}}>
                                <h4 style={{margin:'0 0 5px 0', color: BRAND.charcoal, fontFamily:'Nunito'}}>Clinical Insight</h4>
                                <p style={{margin:0, fontSize:'0.95em', color:'#444', lineHeight:'1.5'}}>
                                    {isSuccess 
                                        ? "Student has met the success criteria threshold. High probability of engagement in mainstream lesson due to established prior knowledge."
                                        : "Student requires additional consolidation. Concepts introduced but retention threshold not yet met. Adaptive teaching recommendation: Re-teach with increased visual cues."}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* 4. CHUNKED CONTENT EVIDENCE */}
                    <div style={{marginBottom:'30px'}} className="pdf-no-split">
                        <h2 style={{ color: BRAND.charcoal, borderBottom: `2px solid ${BRAND.charcoal}`, paddingBottom: '10px', fontFamily: 'Nunito', fontWeight: '800' }}>Evidence of Chunking & Visual Anchoring</h2>
                        <p style={{marginTop:'5px', color:'#666', fontStyle:'italic'}}>Terms in bold indicate "Visual Anchors" used to stabilize reading focus.</p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginBottom:'40px' }}>
                        {tutorial.generatedContent && tutorial.generatedContent.map((chunk, i) => (
                             <div key={i} className="pdf-no-split" style={{ 
                                 background: BRAND.cream, // Pastel background for visual stress
                                 padding: '25px', 
                                 borderRadius: '16px', 
                                 border: `2px solid ${BRAND.teal}`,
                                 boxShadow: '4px 4px 0px rgba(0,0,0,0.05)'
                             }}>
                                <h4 style={{marginTop:0, color: BRAND.teal, textTransform:'uppercase', fontSize:'0.85em', letterSpacing:'1px', fontWeight:'800'}}>
                                    Learning Chunk {i+1}
                                </h4>
                                <div style={{fontSize:'1.05em', lineHeight:'1.8', fontFamily:'Inter', color:'#333'}}>
                                    <ReactMarkdown components={{ 
                                        p: 'div',
                                        strong: ({node, ...props}) => <strong style={{color: BRAND.charcoal, fontWeight:'900', background:'rgba(255,255,255,0.6)', padding:'0 2px', borderRadius:'4px'}} {...props} />
                                    }}>
                                        {chunk.adjustedExplainer}
                                    </ReactMarkdown>
                                </div>
                             </div>
                        ))}
                    </div>

                    {/* 5. STATUTORY EVIDENCE LOG (COMPLIANCE SHIELD) - Added pdf-no-split */}
                    <div style={styles.card} className="pdf-no-split">
                        <h3 style={styles.cardHeader}>Statutory Evidence Log</h3>
                        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(350px, 1fr))', gap:'15px'}}>
                            <div style={styles.complianceBox}>
                                <span style={styles.checkmark}>✓</span>
                                <span>TS5 Evidence: Proves adaptation of teaching to respond to pupil needs.</span>
                            </div>
                            <div style={styles.complianceBox}>
                                <span style={styles.checkmark}>✓</span>
                                <span>Section F Log: Provides proof of specified EHCP support delivery.</span>
                            </div>
                            <div style={styles.complianceBox}>
                                <span style={styles.checkmark}>✓</span>
                                <span>Equality Act 2010: Documents 'Reasonable Adjustments' via dual-format delivery.</span>
                            </div>
                             <div style={styles.complianceBox}>
                                <span style={styles.checkmark}>✓</span>
                                <span>Ofsted Framework: Evidence of 'Quality First Teaching' (QFT) for vulnerable learners.</span>
                            </div>
                        </div>
                    </div>
                    </>}

                    {/* --- RESTORED DEEP DIVE SECTION --- */}
                    {!jcqMode && <>
                    <hr style={{ border: 0, borderTop: `2px dashed ${BRAND.charcoal}`, margin: '50px 0', opacity: 0.3 }} />

                    <h2 style={{ color: '#888', textAlign:'center', fontFamily: 'Nunito', fontWeight: '800', marginBottom: '30px' }} className="pdf-no-split">Deep Dive Analytics</h2>

                    {history.length > 0 && (
                        <div style={{ marginBottom: '40px' }} className="pdf-no-split">
                            <h3 style={{ color: BRAND.charcoal, fontFamily: 'Nunito', fontWeight: '800' }}>Time Series: Performance History</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px', marginTop: '20px' }}>
                                <SimpleBarChart data={historyAccuracy} title="MCQ Accuracy History (%)" color={BRAND.teal} />
                                <SimpleBarChart data={historyGlossaryAttempts} title="Avg Attempts per Key Term" color={BRAND.coral} />
                                <SimpleBarChart data={historyMetacognition} title="Metacognition Score Trend" color={BRAND.warning} />
                                <SimpleBarChart data={historyEngagement} title="Engagement Score Trend" color={BRAND.teal} />
                            </div>
                        </div>
                    )}
                    </>}

                    {jcqMode
                        ? <JCQPerformanceTrackerChart stats={jcqStats} isExtraTimeStudent={jcqIsET} standardMaxSecs={jcqStandardMaxSecs} />
                        : <PerformanceTrackerChart data={performanceData} />
                    }

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '30px', marginTop: '30px' }} className="pdf-no-split">
                        <SimpleBarChart data={mcqTimeData} title="Time Taken: MCQs (seconds)" color={BRAND.teal} yLabel="Seconds" />
                        <SimpleBarChart data={glossaryTimeData} title="Time Taken: Key Terms (seconds)" color={BRAND.coral} yLabel="Seconds" />
                    </div>
                    
                    {/* RESTORED MCQ GRID */}
                    <h3 style={{ color: BRAND.charcoal, fontFamily: 'Nunito', fontWeight: '800', marginTop: '40px', marginBottom: '20px' }} className="pdf-no-split">Question-Level Breakdown</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px', marginBottom:'50px' }}>
                        {mcqAnswers.map((ans, i) => {
                            const isExtraTimeQ = jcqMode && jcqIsET && i >= jcqStandardCutoff;
                            return (
                            <div key={i} className="pdf-no-split" style={{
                                padding: '20px',
                                borderRadius: '12px',
                                border: `2px solid ${isExtraTimeQ ? '#93C5FD' : BRAND.charcoal}`,
                                borderLeft: `8px solid ${ans.isCorrect ? BRAND.teal : BRAND.coral}`,
                                background: isExtraTimeQ ? '#EFF6FF' : '#fff',
                                boxShadow: '4px 4px 0px rgba(0,0,0,0.05)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                            }}>
                                <div>
                                    <div style={{ fontWeight: '800', marginBottom: '10px', color: BRAND.charcoal, fontFamily: 'Nunito' }}>
                                        Q{i+1}: <span style={{fontWeight:'600', fontFamily:'Inter'}}><ReactMarkdown components={{ p: 'span' }}>{ans.questionText}</ReactMarkdown></span>
                                    </div>
                                    <div style={{ fontSize: '0.95em', color: ans.isCorrect ? BRAND.teal : BRAND.coral, marginBottom: '5px', fontWeight: 'bold' }}>
                                        <span style={{color: '#888', fontWeight: 'normal'}}>Student:</span> <ReactMarkdown components={{ p: 'span' }}>{ans.studentAnswer}</ReactMarkdown>
                                    </div>
                                    {!ans.isCorrect && (
                                        <div style={{ fontSize: '0.95em', color: BRAND.charcoal }}>
                                            <span style={{color: '#888'}}>Correct:</span> <strong><ReactMarkdown components={{ p: 'span' }}>{ans.correctAnswer}</ReactMarkdown></strong>
                                        </div>
                                    )}
                                </div>
                                <div style={{ marginTop: '15px', fontSize: '0.85em', color: isExtraTimeQ ? '#1D4ED8' : '#888', textAlign: 'right', fontWeight: '600' }}>
                                    {isExtraTimeQ && <span style={{ marginRight: 6 }}>⏳ Extra time</span>}⏱ {ans.timeTaken}s
                                </div>
                            </div>
                            );
                        })}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PreTeachingReport;
